import { useState, useEffect, useCallback, useRef } from "react";
import {
    Package,
    Copy,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Pencil,
    Check,
    X,
    Plus,
    ExternalLink,
    Home,
    Search,
    Cross,
    Menu,
} from "lucide-react";
import {
    listBundles,
    deleteBundle,
    toggleBundle,
    renameBundle,
    getBundleUrl,
} from "../../utils/api";
import { formatDate } from "../../utils/fileHelpers";
import BundleCreator from "../../components/BundleCreator/BundleCreator";

import "./BundlePage.css";

export default function BundlesPage({ onBack }) {
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreator, setShowCreator] = useState(false);
    const [toast, setToast] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [search, setSearch] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const searchRef = useRef();

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listBundles();
            setBundles(res.data);
        } catch {
            showToast("Failed to load bundles", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filteredBundles = search.trim()
        ? bundles.filter((b) =>
              b.name.toLowerCase().includes(search.toLowerCase()),
          )
        : bundles;

    const handleCopy = (token) => {
        navigator.clipboard.writeText(getBundleUrl(token));
        showToast("Link copied!");
    };

    const handleToggle = async (bundle) => {
        try {
            await toggleBundle(bundle._id);
            showToast(
                bundle.active ? "Bundle deactivated" : "Bundle activated",
            );
            load();
        } catch {
            showToast("Failed to toggle", "error");
        }
    };

    const handleDelete = async (bundle) => {
        if (
            !window.confirm(
                `Permanently delete "${bundle.name}"? The link will stop working.`,
            )
        )
            return;
        try {
            await deleteBundle(bundle._id);
            showToast("Bundle deleted");
            load();
        } catch {
            showToast("Delete failed", "error");
        }
    };

    const startRename = (bundle) => {
        setRenamingId(bundle._id);
        setRenameValue(bundle.name);
    };

    const commitRename = async (bundle) => {
        if (!renameValue.trim()) {
            setRenamingId(null);
            return;
        }
        try {
            await renameBundle(bundle._id, renameValue.trim());
            showToast("Renamed");
            setRenamingId(null);
            load();
        } catch {
            showToast("Rename failed", "error");
        }
    };

    return (
        <div className="explorer">
            {/* Mobile backdrop */}
            <div
                className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
                onClick={() => setSidebarOpen(false)}
            />

            <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
                <div className="sidebar-logo">
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            backgroundColor: "#00a854",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Cross size={15} color="#fff" />
                    </div>
                    <span>Brian Balanoff</span>
                    <button
                        className="sidebar-close-btn"
                        onClick={() => setSidebarOpen(false)}
                        title="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>
                <nav className="sidebar-nav">
                    <button
                        className="nav-item"
                        onClick={() => {
                            onBack();
                            setSidebarOpen(false);
                        }}
                    >
                        <Home size={18} /> My Files
                    </button>
                    <button className="nav-item active">
                        <Package size={18} /> Bundles
                    </button>
                </nav>
                <div className="sidebar-actions">
                    <button
                        className="btn-upload"
                        onClick={() => {
                            setShowCreator(true);
                            setSidebarOpen(false);
                        }}
                    >
                        <Plus size={16} /> New Bundle
                    </button>
                </div>
            </aside>

            <main className="explorer-main">
                <div className="explorer-toolbar">
                    <button
                        className="mobile-menu-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSidebarOpen(true);
                        }}
                        title="Open menu"
                    >
                        <Menu size={18} />
                    </button>
                    <div className="breadcrumbs">
                        <button className="crumb" onClick={onBack}>
                            My Files
                        </button>
                        <span className="crumb-sep">/</span>
                        <button className="crumb active">Bundles</button>
                    </div>

                    <div className="toolbar-right">
                        <div className="toolbar-search">
                            <Search size={14} className="toolbar-search-icon" />
                            <input
                                ref={searchRef}
                                className="toolbar-search-input"
                                placeholder="Search bundles…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button
                                    className="toolbar-search-clear"
                                    onClick={() => {
                                        setSearch("");
                                        searchRef.current?.focus();
                                    }}
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                        <button
                            className="btn-primary"
                            style={{
                                fontSize: 13,
                                padding: "7px 16px",
                                whiteSpace: "nowrap",
                            }}
                            onClick={() => setShowCreator(true)}
                        >
                            <Plus
                                size={14}
                                style={{
                                    display: "inline",
                                    marginRight: 6,
                                    verticalAlign: "middle",
                                }}
                            />
                            New Bundle
                        </button>
                    </div>
                </div>

                <div className="bundles-page">
                    {loading ? (
                        <div className="bundles-empty">
                            <div>Loading…</div>
                        </div>
                    ) : bundles.length === 0 ? (
                        <div className="bundles-empty">
                            <Package size={48} />
                            <div>No bundles yet</div>
                            <div className="empty-hint">
                                Share multiple files at once with a single link
                            </div>
                            <button
                                className="btn-primary"
                                style={{ marginTop: 8 }}
                                onClick={() => setShowCreator(true)}
                            >
                                Create your first bundle
                            </button>
                        </div>
                    ) : filteredBundles.length === 0 ? (
                        <div className="bundles-empty">
                            <Package size={48} />
                            <div>No results for "{search}"</div>
                        </div>
                    ) : (
                        <div className="bundles-list">
                            {filteredBundles.map((bundle) => (
                                <div
                                    key={bundle._id}
                                    className={`bundle-card ${!bundle.active ? "inactive" : ""}`}
                                >
                                    <div className="bundle-card-header">
                                        {renamingId === bundle._id ? (
                                            <div className="bundle-rename-row">
                                                <input
                                                    className="bundle-rename-input"
                                                    value={renameValue}
                                                    onChange={(e) =>
                                                        setRenameValue(
                                                            e.target.value,
                                                        )
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter")
                                                            commitRename(
                                                                bundle,
                                                            );
                                                        if (e.key === "Escape")
                                                            setRenamingId(null);
                                                    }}
                                                    autoFocus
                                                />
                                                <button
                                                    className="bundle-icon-btn confirm"
                                                    onClick={() =>
                                                        commitRename(bundle)
                                                    }
                                                >
                                                    <Check size={15} />
                                                </button>
                                                <button
                                                    className="bundle-icon-btn"
                                                    onClick={() =>
                                                        setRenamingId(null)
                                                    }
                                                >
                                                    <X size={15} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bundle-card-title">
                                                <Package
                                                    size={18}
                                                    className="bundle-card-icon"
                                                />
                                                <span>{bundle.name}</span>
                                                {!bundle.active && (
                                                    <span className="bundle-inactive-badge">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className="bundle-card-meta">
                                            {bundle.fileIds?.length ?? 0} file
                                            {(bundle.fileIds?.length ?? 0) !== 1
                                                ? "s"
                                                : ""}
                                            {" · "}
                                            Created{" "}
                                            {formatDate(bundle.createdAt)}
                                        </div>
                                    </div>

                                    <div className="bundle-card-link">
                                        <input
                                            readOnly
                                            className={`share-link-input ${!bundle.active ? "revoked" : ""}`}
                                            value={
                                                bundle.active
                                                    ? getBundleUrl(
                                                          bundle.shareToken,
                                                      )
                                                    : "— link inactive —"
                                            }
                                        />
                                    </div>

                                    <div className="bundle-card-actions">
                                        <button
                                            className="bundle-action-pill"
                                            onClick={() =>
                                                handleCopy(bundle.shareToken)
                                            }
                                            disabled={!bundle.active}
                                            title="Copy link"
                                        >
                                            <Copy size={14} /> Copy Link
                                        </button>
                                        <a
                                            href={getBundleUrl(
                                                bundle.shareToken,
                                            )}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`bundle-action-pill ${!bundle.active ? "disabled-link" : ""}`}
                                            onClick={(e) =>
                                                !bundle.active &&
                                                e.preventDefault()
                                            }
                                            title="Preview bundle page"
                                        >
                                            <ExternalLink size={14} /> Preview
                                        </a>
                                        <button
                                            className="bundle-action-pill"
                                            onClick={() => startRename(bundle)}
                                            title="Rename"
                                        >
                                            <Pencil size={14} /> Rename
                                        </button>
                                        <button
                                            className={`bundle-action-pill ${bundle.active ? "warn" : "ok"}`}
                                            onClick={() => handleToggle(bundle)}
                                            title={
                                                bundle.active
                                                    ? "Deactivate link"
                                                    : "Reactivate link"
                                            }
                                        >
                                            {bundle.active ? (
                                                <>
                                                    <ToggleRight size={14} />{" "}
                                                    Deactivate
                                                </>
                                            ) : (
                                                <>
                                                    <ToggleLeft size={14} />{" "}
                                                    Reactivate
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="bundle-action-pill danger"
                                            onClick={() => handleDelete(bundle)}
                                            title="Delete permanently"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {showCreator && (
                <BundleCreator
                    onClose={() => setShowCreator(false)}
                    onCreated={load}
                />
            )}
            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
        </div>
    );
}
